import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Member } from '../../types';
import { useIsAdmin, useIsManager, useGustoEnabled, useMemberGustoData } from '../../hooks';
import { GustoUserAssignment } from './GustoUserAssignment';
import { GustoInfoCard } from './GustoInfoCard';
import { GustoPaymentHistoryCard } from './GustoPaymentHistoryCard';

interface GustoUserCardProps {
  person: Member;
}

/**
 * GustoUserCard
 * Standalone module (like the Reporting Chain) shown in a member's Gusto tab.
 * Surfaces the Gusto user assignment control plus a read-only view of the
 * member's synced Gusto payroll/employment data. Only visible to admins and
 * managers, and only when the organization's Gusto connection is active.
 */
export const GustoUserCard: React.FC<GustoUserCardProps> = ({ person }) => {
  const { t } = useTranslation('people');
  const { isAdmin } = useIsAdmin();
  const { isManager } = useIsManager();
  const { isConnected } = useGustoEnabled();
  const canView = isAdmin || isManager;
  const { data, isLoading, error } = useMemberGustoData(person.id, canView && isConnected);

  if (!canView) return null;
  if (!isConnected) return null;

  return (
    <div className="space-y-6" data-testid="gusto-user-card">
      <Card data-testid="gusto-user-assignment-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            {t('form.labels.gustoUser')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <GustoUserAssignment person={person} />
        </CardContent>
      </Card>

      <GustoInfoCard
        data={data}
        isLoading={isLoading}
        error={error}
        testIdPrefix="member-gusto-info"
        descriptionKey="detail.gusto.memberDescription"
        notConnectedDescriptionKey="detail.gusto.memberNotConnectedDescription"
      />

      <GustoPaymentHistoryCard data={data} />
    </div>
  );
};
